import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { GOV_EVENT_KINDS } from '../components/calendarGovStyles';

// Event type config
const EVENT_TYPES: Record<string, { color: string; icon: string; label: string }> = {
  meeting: { color: '#3B82F6', icon: '👥', label: 'Meeting' },
  interview: { color: '#8B5CF6', icon: '🎤', label: 'Interview' },
  follow_up: { color: '#F97316', icon: '📞', label: 'Follow Up' },
  reminder: { color: '#EAB308', icon: '⏰', label: 'Reminder' },
  training: { color: '#22C55E', icon: '📚', label: 'Training' },
  other: { color: '#6B7280', icon: '📅', label: 'Other' },
  google: { color: '#4285F4', icon: '📆', label: 'Google' },
};

const JOB_TYPES: Record<string, { bg: string; text: string; label: string; icon: string }> = {
  asphalt:  { bg: '#F59E0B', text: '#fff', label: 'Asphalt', icon: '🛣️' },
  concrete: { bg: '#8B5CF6', text: '#fff', label: 'Concrete', icon: '🪨' },
  roofing:  { bg: '#4A7EB5', text: '#fff', label: 'Roofing', icon: '🏠' },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

interface TeamCalendar {
  id: string;
  calendar_id: string;
  display_name: string;
  color: string;
  icon: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  event_type: string;
  event_date: string;
  event_time?: string;
  end_time?: string;
  all_day?: boolean;
  location?: string;
  calendar_id?: string;
  calendar_name?: string;
  calendar_color?: string;
  calendar_icon?: string;
  html_link?: string;
  [key: string]: any;
}

interface CalendarItem {
  id: string;
  type: 'job' | 'event';
  title: string;
  date: string;
  time?: string;
  color: string;
  icon: string;
  jobType?: string;
  calendarId?: string;
  data: any;
}

interface Filters {
  jobs: Record<string, boolean>;
  crmEvents: boolean;
  govSolicitations: boolean;
  govConstruction: boolean;
  calendars: Record<string, boolean>;
}

const loadFilters = (): Filters => {
  try {
    const saved = localStorage.getItem('calendar_filters');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Backfill new keys for users with older cached state
      return {
        govSolicitations: true,
        govConstruction: true,
        ...parsed,
      };
    }
  } catch {}
  return {
    jobs: { asphalt: true, concrete: true, roofing: true },
    crmEvents: true,
    govSolicitations: true,
    govConstruction: true,
    calendars: {},
  };
};

const saveFilters = (filters: Filters) => {
  localStorage.setItem('calendar_filters', JSON.stringify(filters));
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<any[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [googleEvents, setGoogleEvents] = useState<CalendarEvent[]>([]);
  const [govSolicitations, setGovSolicitations] = useState<any[]>([]);
  const [govConstruction, setGovConstruction] = useState<any[]>([]);
  const [teamCalendars, setTeamCalendars] = useState<TeamCalendar[]>([]);
  const [today] = useState(new Date());
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>(loadFilters);
  const filterRef = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'meeting',
    event_date: '',
    event_time: '',
    end_time: '',
    all_day: false,
    location: '',
    visibility: 'team',
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // Update calendar filters when team calendars are loaded
  useEffect(() => {
    if (teamCalendars.length > 0) {
      setFilters(prev => {
        const updated = { ...prev, calendars: { ...prev.calendars } };
        teamCalendars.forEach(tc => {
          if (!(tc.calendar_id in updated.calendars)) {
            updated.calendars[tc.calendar_id] = true;
          }
        });
        return updated;
      });
    }
  }, [teamCalendars]);

  // Save filters on change
  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const fetchData = useCallback(async () => {
    try {
      const year = current.getFullYear();
      const month = current.getMonth();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`;

      const [jobsRes, eventsRes, googleRes] = await Promise.all([
        api.get('/jobs'),
        api.get(`/calendar-events?start=${start}&end=${end}&include_gov=true`),
        api.get(`/calendar-events/google?start=${start}&end=${end}`).catch(() => ({ data: { events: [], calendars: [] } })),
      ]);

      setJobs((jobsRes.data || []).filter((j: any) => j.scheduled_date));
      setEvents(eventsRes.data?.events || []);
      setGovSolicitations(eventsRes.data?.gov_solicitations || []);
      setGovConstruction(eventsRes.data?.gov_construction || []);
      setTeamCalendars(googleRes.data?.calendars || []);
      
      // Filter Google events to exclude ones already synced to CRM
      const crmGoogleIds = new Set((eventsRes.data?.events || []).map((e: any) => e.google_event_id).filter(Boolean));
      const uniqueGoogleEvents = (googleRes.data?.events || []).filter((e: any) => !crmGoogleIds.has(e.id));
      setGoogleEvents(uniqueGoogleEvents);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [current]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const year = current.getFullYear();
  const month = current.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();

  // Build grid
  const cells: { date: Date; current: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, daysInPrev - i), current: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), current: true });
  }
  while (cells.length < 42) {
    cells.push({ date: new Date(year, month + 1, cells.length - daysInMonth - firstDay + 1), current: false });
  }

  // Get items for a date with filters applied
  const getItemsForDate = (date: Date): CalendarItem[] => {
    const dateStr = date.toISOString().split('T')[0];
    const items: CalendarItem[] = [];

    // Add jobs (filtered by type)
    jobs.filter(j => j.scheduled_date === dateStr).forEach(job => {
      const jobType = job.job_type || 'roofing';
      if (!filters.jobs[jobType]) return;
      const typeConfig = JOB_TYPES[jobType] || JOB_TYPES.roofing;
      items.push({
        id: `job-${job.id}`,
        type: 'job',
        title: job.leads?.homeowner_name || job.homeowner_name || 'Job',
        date: dateStr,
        time: job.scheduled_time,
        color: typeConfig.bg,
        icon: typeConfig.icon,
        jobType,
        data: job,
      });
    });

    // Add CRM events — route gov-linked events through gov styling, generic through crmEvents filter
    events.filter(e => e.event_date === dateStr).forEach(event => {
      const relType = (event as any).related_entity_type;
      const eventKind = (event as any).event_kind;
      const isGovSol = relType === 'gov_solicitation';
      const isGovCon = relType === 'gov_construction_job';

      if (isGovSol && !filters.govSolicitations) return;
      if (isGovCon && !filters.govConstruction) return;
      if (!isGovSol && !isGovCon && !filters.crmEvents) return;

      const govCfg = (isGovSol || isGovCon) && eventKind ? GOV_EVENT_KINDS[eventKind] : null;
      const typeConfig = govCfg || EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
      items.push({
        id: `event-${event.id}`,
        type: 'event',
        title: event.title,
        date: dateStr,
        time: event.event_time || undefined,
        color: (event as any).color || typeConfig.color,
        icon: typeConfig.icon,
        data: { ...event, _govKind: govCfg ? eventKind : undefined },
      });
    });

    // Add gov solicitation deadlines (raw pulls that never got auto-scheduled)
    if (filters.govSolicitations) {
      govSolicitations.filter(e => e.event_date === dateStr).forEach(event => {
        const cfg = GOV_EVENT_KINDS[event.event_kind] || GOV_EVENT_KINDS.other;
        items.push({
          id: event.id,
          type: 'event',
          title: event.title,
          date: dateStr,
          time: event.event_time || undefined,
          color: cfg.color,
          icon: cfg.icon,
          data: { ...event, _govKind: event.event_kind },
        });
      });
    }

    // Add gov construction milestones
    if (filters.govConstruction) {
      govConstruction.filter(e => e.event_date === dateStr).forEach(event => {
        const cfg = GOV_EVENT_KINDS[event.event_kind] || GOV_EVENT_KINDS.other;
        items.push({
          id: event.id,
          type: 'event',
          title: event.title,
          date: dateStr,
          time: event.event_time || undefined,
          color: cfg.color,
          icon: cfg.icon,
          data: { ...event, _govKind: event.event_kind },
        });
      });
    }

    // Add Google events (filtered by calendar)
    googleEvents.filter(e => e.event_date === dateStr).forEach(event => {
      if (event.calendar_id && !filters.calendars[event.calendar_id]) return;
      items.push({
        id: `google-${event.id}`,
        type: 'event',
        title: event.title,
        date: dateStr,
        time: event.event_time || undefined,
        color: event.calendar_color || '#4285F4',
        icon: event.calendar_icon || '📆',
        calendarId: event.calendar_id,
        data: { ...event, _source: 'google', _readonly: true },
      });
    });

    return items.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return a.time.localeCompare(b.time);
    });
  };

  const isToday = (date: Date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  // Toggle filter helpers
  const toggleJobType = (type: string) => {
    setFilters(prev => ({ ...prev, jobs: { ...prev.jobs, [type]: !prev.jobs[type] } }));
  };

  const toggleAllJobs = () => {
    const allOn = Object.values(filters.jobs).every(v => v);
    const newState = Object.fromEntries(Object.keys(filters.jobs).map(k => [k, !allOn]));
    setFilters(prev => ({ ...prev, jobs: newState }));
  };

  const toggleCrmEvents = () => {
    setFilters(prev => ({ ...prev, crmEvents: !prev.crmEvents }));
  };

  const toggleGovSolicitations = () => {
    setFilters(prev => ({ ...prev, govSolicitations: !prev.govSolicitations }));
  };

  const toggleGovConstruction = () => {
    setFilters(prev => ({ ...prev, govConstruction: !prev.govConstruction }));
  };

  const toggleCalendar = (calId: string) => {
    setFilters(prev => ({ ...prev, calendars: { ...prev.calendars, [calId]: !prev.calendars[calId] } }));
  };

  const toggleAllCalendars = () => {
    const allOn = Object.values(filters.calendars).every(v => v);
    const newState = Object.fromEntries(Object.keys(filters.calendars).map(k => [k, !allOn]));
    setFilters(prev => ({ ...prev, calendars: newState }));
  };

  // Modal handlers
  const openCreateModal = (date?: string) => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      event_type: 'meeting',
      event_date: date || selectedDate || new Date().toISOString().split('T')[0],
      event_time: '',
      end_time: '',
      all_day: false,
      location: '',
      visibility: 'team',
    });
    setShowModal(true);
  };

  const openEditModal = (event: CalendarEvent) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      event_date: event.event_date,
      event_time: event.event_time || '',
      end_time: event.end_time || '',
      all_day: event.all_day || false,
      location: event.location || '',
      visibility: (event as any).visibility || 'team',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await api.patch(`/calendar-events/${editingEvent.id}`, formData);
      } else {
        await api.post('/calendar-events', formData);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
      alert('Failed to save event');
    }
  };

  const handleDelete = async () => {
    if (!editingEvent) return;
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.delete(`/calendar-events/${editingEvent.id}`);
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
      alert('Failed to delete event');
    }
  };

  const formatTime = (time?: string) => {
    if (!time) return '';
    const match = time.match(/^(\d{2}):(\d{2})/);
    if (!match) return time;
    const h = parseInt(match[1]);
    const m = match[2];
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${suffix}`;
  };

  const selectedItems = selectedDate ? getItemsForDate(new Date(selectedDate + 'T12:00:00')) : [];

  // Count active items for header
  const activeJobCount = jobs.filter(j => {
    const d = new Date(j.scheduled_date);
    return d.getMonth() === month && d.getFullYear() === year && filters.jobs[j.job_type || 'roofing'];
  }).length;

  const Checkbox = ({ checked, onChange, label, color }: { checked: boolean; onChange: () => void; label: string; color?: string }) => (
    <div onClick={onChange} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
      <div style={{
        width: 18, height: 18, borderRadius: 4,
        border: checked ? 'none' : '2px solid #475569',
        background: checked ? (color || '#4A7EB5') : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s',
      }}>
        {checked && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
      </div>
      <span style={{ color: '#e2e8f0', fontSize: 13 }}>{label}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100%', background: 'transparent' }}>
      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 24, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ color: '#f1f5f9', fontSize: 24, fontWeight: 800, margin: 0 }}>Calendar</h1>
            <span style={{ color: '#64748b', fontSize: 13 }}>{activeJobCount} jobs visible</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setCurrent(new Date(year, month - 1, 1))} style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 16 }}>←</button>
            <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 18, minWidth: 180, textAlign: 'center' }}>{MONTHS[month]} {year}</span>
            <button onClick={() => setCurrent(new Date(year, month + 1, 1))} style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 16 }}>→</button>
            <button onClick={() => setCurrent(new Date(today.getFullYear(), today.getMonth(), 1))} style={{ background: '#334155', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Today</button>
            
            {/* Filter Dropdown */}
            <div ref={filterRef} style={{ position: 'relative' }}>
              <button onClick={() => setShowFilters(!showFilters)} style={{ background: showFilters ? '#4A7EB5' : '#334155', border: 'none', color: '#f1f5f9', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                ☰ Filters <span style={{ fontSize: 10 }}>{showFilters ? '▲' : '▼'}</span>
              </button>
              
              {showFilters && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 240, background: '#1e293b', borderRadius: 10, border: '1px solid #334155', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', zIndex: 100, padding: 16, maxHeight: 400, overflowY: 'auto' }}>
                  {/* Jobs Section */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Jobs</span>
                      <button onClick={toggleAllJobs} style={{ background: 'none', border: 'none', color: '#4A7EB5', cursor: 'pointer', fontSize: 11 }}>
                        {Object.values(filters.jobs).every(v => v) ? 'Hide all' : 'Show all'}
                      </button>
                    </div>
                    {Object.entries(JOB_TYPES).map(([type, config]) => (
                      <Checkbox key={type} checked={filters.jobs[type] ?? true} onChange={() => toggleJobType(type)} label={`${config.icon} ${config.label}`} color={config.bg} />
                    ))}
                  </div>

                  {/* CRM Events Section */}
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>CRM Events</span>
                    <Checkbox checked={filters.crmEvents} onChange={toggleCrmEvents} label="📅 Custom Events" color="#3B82F6" />
                  </div>

                  {/* Government Section */}
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>Government</span>
                    <Checkbox checked={filters.govSolicitations} onChange={toggleGovSolicitations} label="📑 Solicitation Deadlines" color="#DC2626" />
                    <Checkbox checked={filters.govConstruction} onChange={toggleGovConstruction} label="🏛️ Construction Milestones" color="#3B82F6" />
                  </div>

                  {/* Team Calendars Section */}
                  {teamCalendars.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Team Calendars</span>
                        <button onClick={toggleAllCalendars} style={{ background: 'none', border: 'none', color: '#4A7EB5', cursor: 'pointer', fontSize: 11 }}>
                          {Object.values(filters.calendars).every(v => v) ? 'Hide all' : 'Show all'}
                        </button>
                      </div>
                      {teamCalendars.map(tc => (
                        <Checkbox key={tc.calendar_id} checked={filters.calendars[tc.calendar_id] ?? true} onChange={() => toggleCalendar(tc.calendar_id)} label={`${tc.icon} ${tc.display_name}`} color={tc.color} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button onClick={() => openCreateModal()} style={{ background: '#4A7EB5', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ Event</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, flex: 1, minHeight: 0 }}>
          {/* Calendar Grid */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 1 }}>
              {DAYS.map(d => (
                <div key={d} style={{ padding: '10px 0', textAlign: 'center', color: '#64748b', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{d}</div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)', gap: 1, flex: 1 }}>
              {cells.map((cell, i) => {
                const dateStr = cell.date.toISOString().split('T')[0];
                const dayItems = getItemsForDate(cell.date);
                const todayCell = isToday(cell.date);
                const isSelected = dateStr === selectedDate;
                return (
                  <div key={i} onClick={() => setSelectedDate(dateStr)} style={{
                    background: todayCell ? '#243447' : cell.current ? '#1e293b' : '#172231',
                    border: isSelected ? '2px solid #4A7EB5' : todayCell ? '1px solid #4A7EB5' : '1px solid #1e293b',
                    padding: '6px 8px', overflow: 'hidden', cursor: 'pointer',
                  }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 24, height: 24, borderRadius: '50%',
                      background: todayCell ? '#4A7EB5' : 'transparent',
                      color: todayCell ? '#fff' : cell.current ? '#f1f5f9' : '#334155',
                      fontSize: 12, fontWeight: todayCell ? 800 : 400, marginBottom: 4,
                    }}>{cell.date.getDate()}</div>
                    {dayItems.slice(0, 3).map((item) => (
                      <div key={item.id} style={{
                        background: item.color, color: '#fff', borderRadius: 4, padding: '2px 6px',
                        fontSize: 10, fontWeight: 600, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }} title={`${item.icon} ${item.title}`}>{item.icon} {item.title}</div>
                    ))}
                    {dayItems.length > 3 && <div style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>+{dayItems.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar - Selected Day */}
          <div style={{ width: 320, background: '#1e293b', borderRadius: 12, padding: 16, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 700, margin: 0 }}>
                {selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Select a date'}
              </h2>
              {selectedDate && (
                <button onClick={() => openCreateModal(selectedDate)} style={{ background: '#4A7EB5', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+</button>
              )}
            </div>

            {selectedItems.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>
                {selectedDate ? 'No events on this day' : 'Click a date to view details'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selectedItems.map(item => (
                  <div key={item.id} onClick={() => {
                    if (item.type === 'job') navigate(`/pipeline/${item.data.id}`);
                    else if (item.data._source === 'google' && item.data.html_link) window.open(item.data.html_link, '_blank');
                    else if (item.data.related_entity_type === 'gov_solicitation') navigate(`/gov/solicitations/${item.data.related_entity_id}`);
                    else if (item.data.related_entity_type === 'gov_construction_job') navigate(`/gov/construction/${item.data.related_entity_id}`);
                    else openEditModal(item.data);
                  }} style={{ background: '#172231', borderRadius: 8, padding: 12, cursor: 'pointer', borderLeft: `4px solid ${item.color}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>{item.icon}</span>
                      <span style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600, flex: 1 }}>{item.title}</span>
                      {item.time && <span style={{ color: '#4A7EB5', fontSize: 12, fontWeight: 600 }}>{formatTime(item.time)}</span>}
                    </div>
                    {item.type === 'job' && <div style={{ color: '#94a3b8', fontSize: 12 }}>📍 {item.data.leads?.address || item.data.address || 'Address pending'}</div>}
                    {item.type === 'event' && item.data.location && <div style={{ color: '#94a3b8', fontSize: 12 }}>📍 {item.data.location}</div>}
                    {item.type === 'event' && item.data.description && !item.data._source && <div style={{ color: '#64748b', fontSize: 11, marginTop: 4, maxHeight: 40, overflow: 'hidden' }}>{item.data.description}</div>}
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ background: item.color + '33', color: item.color, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                        {item.type === 'job' ? item.data.job_type : (item.data.calendar_name || item.data.event_type)}
                      </span>
                      {item.data._source === 'google' && <span style={{ color: '#64748b', fontSize: 10 }}>• Google</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowModal(false)}>
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, width: 420, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{editingEvent ? 'Edit Event' : 'New Event'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Title *</label>
                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }} placeholder="Meeting with client..." />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Type</label>
                  <select value={formData.event_type} onChange={e => setFormData({ ...formData, event_type: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }}>
                    {Object.entries(EVENT_TYPES).filter(([k]) => k !== 'google').map(([type, { icon, label }]) => (
                      <option key={type} value={type}>{icon} {label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Visibility</label>
                  <select value={formData.visibility} onChange={e => setFormData({ ...formData, visibility: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }}>
                    <option value="private">🔒 Private</option>
                    <option value="team">👥 Team</option>
                    <option value="company">🏢 Company</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Date *</label>
                <input type="date" value={formData.event_date} onChange={e => setFormData({ ...formData, event_date: e.target.value })} required style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <input type="checkbox" id="all_day" checked={formData.all_day} onChange={e => setFormData({ ...formData, all_day: e.target.checked, event_time: '', end_time: '' })} style={{ width: 18, height: 18 }} />
                <label htmlFor="all_day" style={{ color: '#f1f5f9', fontSize: 14 }}>All day event</label>
              </div>

              {!formData.all_day && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Start Time</label>
                    <input type="time" value={formData.event_time} onChange={e => setFormData({ ...formData, event_time: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }} />
                  </div>
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>End Time</label>
                    <input type="time" value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }} />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Location</label>
                <input type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }} placeholder="Office, Zoom, etc..." />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Description</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} style={{ width: '100%', padding: '10px 12px', background: '#1A2B3C', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14, resize: 'vertical' }} placeholder="Notes about the event..." />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                {editingEvent && <button type="button" onClick={handleDelete} style={{ padding: '10px 16px', background: '#dc2626', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Delete</button>}
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', background: '#334155', border: 'none', borderRadius: 8, color: '#f1f5f9', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 20px', background: '#4A7EB5', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{editingEvent ? 'Save' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
